import React, { ReactElement, useState } from 'react';
import { ImageWithInfo } from '@kursk/components/ui/ImageWithInfo/ImageWithInfo';
import classNames from 'classnames';
import { getAddress } from '@kursk/utils/getAddress';
import styles from './Widget.module.scss';
import { IWidget } from "@common/redux/types/event";
import { BuyButton } from "@kursk/components/ui/BuyButton/BuyButton";
import { ShowMoreIcon } from "@kursk/components/ui/icons";
import { YMap } from "@kursk/components/ui/YMap/YMap";
import { isTicketsInfoAvailable } from "@common/utils/tickets";
import { LdJson } from "@kursk/components/seo/LdJson";

interface IProps {
  data: IWidget;
  type: 'place' | 'article' | 'event' | 'touristRoute' | 'code';
  entityDescription?: string;
  code?: string;
  index: number;
}

const buyTypesHash = {
  place: 'places',
  article: 'overviews',
  event: 'events',
}

export const Widget = ({ data, type, entityDescription, code, index }: IProps): ReactElement => {
  const [isShown, setIsShown] = useState(false);
  const { description, name, image, sysName, _id, isPublished, mapPosition } = data;

  const showMore = () => setIsShown(!isShown);

  const checkIsTicketsAvailable = () => {
    switch (type) {
      case 'event': {
        return data.actualSeances?.all.find(info => isTicketsInfoAvailable(info.ticketsInfo)) || data.externalSaleLink
      }
      case 'place' :
      case 'article' : {
        return isTicketsInfoAvailable(data.ticketsInfo) || data.externalSaleLink
      }
    }

    return false;
  }

  return (
    <section className={styles.content}>
      {type === 'event' && <LdJson
        entity={data}
        type={'events'}
      />}
      <div className={styles.wrapper} id='widget-code'>

        <div className={styles.description}>
          <div>
            {isPublished ? (
              <a href={`/${type.toLowerCase()}s/${_id}/${sysName}`} target='_blank' rel="noreferrer">
                <h3 className={styles.header}>
                  {name}
                </h3>
              </a>
            ) : (
              <h3 className={styles.header}>
                {name}
              </h3>
            )}
            {(entityDescription || description) && (
              <p className={styles.name}>
                {entityDescription || description}
              </p>
            )}
          </div>
          {data.address && (
            <div className={styles.addressWrapper}>
              <button
                className={styles.button}
                onClick={() => showMore()}>
                <div className={styles.address}>
                  {getAddress(data.address)}
                  <ShowMoreIcon
                    classes={classNames([styles.showMoreIcon, isShown && styles.rotate])}
                    width={12}
                    height={5}
                  />
                </div>
              </button>
            </div>
          )}
        </div>
        {buyTypesHash[type] && checkIsTicketsAvailable() && (
          <BuyButton
            entityType={buyTypesHash[type]}
            entityId={data._id}
            wrapperClassName={styles.buyButtonWrap}
            hintBackgroundTheme="light"
            dataTestId={`buy-button-widget-${index}`}
          />
        )}
        {typeof window !== "undefined" && <div className={styles.iframe} dangerouslySetInnerHTML={{ __html: code }}/>}
      </div>
      <div className={styles.imageWrapper}>
        <div className={styles.image}>
          <ImageWithInfo
            image={image}
            className={styles.imageWithInfo}
            srcSet={[
              {
                media: '(min-width: 768px)',
                width: 640,
                height: 424,
              },
              {
                media: '(max-width: 767px)',
                width: 670,
                height: 448,
              }
            ]}
          />
        </div>
      </div>
      {isShown && (
        <div className={styles.mapWrapper}>
          <YMap
            mapPosition={mapPosition}
            placeName={name}
          />
        </div>
      )}
    </section>
  );
};
